/**
 * creations.jsx — everything Rammy has finished making for you, kept.
 *
 * Studio renders are pushed to object storage the moment they exist, so a
 * closed tab no longer costs you the piece. This screen plays them back,
 * downloads them, and re-opens the project that made them.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import ShareSheet from "@/components/studio/ShareSheet";
import * as L from "lucide-react";

const fmtDur = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, "0")}`;
};
const fmtStamp = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};
const fmtSize = (b) => {
  if (!b) return "";
  return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " · " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export function CreationsScreen({ go }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");   // all | audio | video
  const [activeUrl, setActiveUrl] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/creations");
      setItems(data.creations || []);
    } catch (_) {
      toast.error("Couldn't load your creations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => { if (activeUrl) URL.revokeObjectURL(activeUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBlobUrl = async (id) => {
    const res = await api.get(`/studio/creations/${id}/file`, { responseType: "blob", timeout: 120000 });
    return URL.createObjectURL(res.data);
  };

  const open = async (item) => {
    if (activeId === item.id) {
      try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
      setActiveId(null);
      return;
    }
    setBusyId(item.id);
    try {
      const url = await fetchBlobUrl(item.id);
      if (activeUrl) URL.revokeObjectURL(activeUrl);
      setActiveUrl(url);
      setActiveId(item.id);
    } catch (_) {
      toast.error("Couldn't fetch that file");
    } finally {
      setBusyId(null);
    }
  };

  const download = async (item) => {
    setBusyId(item.id);
    try {
      const url = await fetchBlobUrl(item.id);
      const ext = item.kind === "image"
        ? (item.content_type?.includes("jpeg") ? "jpg" : "png")
        : item.kind === "video" ? "mp4" : (item.content_type?.includes("wav") ? "wav" : "mp3");
      const slug = (item.title || "rammy").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "rammy";
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (_) {
      toast.error("Couldn't download that file");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (item) => {
    setConfirmId(null);
    try {
      await api.delete(`/studio/creations/${item.id}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
      if (activeId === item.id) setActiveId(null);
      toast.success("Removed");
    } catch (_) {
      toast.error("Couldn't remove that");
    }
  };

  const shown = items.filter(i => filter === "all" || i.kind === filter);

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6" data-testid="screen-creations">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl">Creations</h1>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
            Every piece Rammy finished, kept for you — audio, video and pictures, here forever.
          </p>
        </div>
        <div className="inline-flex rounded-full bg-[var(--rammy-surface-alt)] p-1" data-testid="creations-filter">
          {[
            { id: "all", label: "All" },
            { id: "audio", label: "Audio" },
            { id: "video", label: "Video" },
            { id: "image", label: "Images" },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-active={filter === f.id ? "true" : "false"}
              data-testid={`creations-filter-${f.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.id ? "bg-[var(--rm-surface)] text-[var(--rammy-text)] shadow-sm" : "text-[var(--rammy-text-sec)]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-[var(--rammy-text-muted)]">Loading…</div>}

      {!loading && shown.length === 0 && (
        <div className="rounded-3xl border border-dashed border-[var(--rammy-border)] p-10 text-center" data-testid="creations-empty">
          <div className="w-12 h-12 rounded-2xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center mx-auto mb-4">
            <L.AudioLines size={20}/>
          </div>
          <div className="font-display text-xl">Nothing finished yet</div>
          <div className="text-sm text-[var(--rammy-text-sec)] mt-1 max-w-md mx-auto">
            Render something in Studio and it lands here automatically — no saving, no lost tabs.
          </div>
          <Button onClick={() => go?.("studio")} className="btn-pill mt-5 h-9 text-xs" data-testid="creations-go-studio">
            <L.Sparkles size={13}/> Open Studio
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {shown.map(item => (
          <div
            key={item.id}
            className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4"
            data-testid={`creation-${item.id}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                item.kind === "video"
                  ? "bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)]"
                  : item.kind === "image"
                    ? "bg-[#7C6BF5]/15 text-[#5B4BD6]"
                    : "bg-[#E89A3C]/15 text-[#B4741F]"
              }`}>
                {item.kind === "video" ? <L.Film size={17}/>
                  : item.kind === "image" ? <L.Image size={17}/>
                  : <L.FileAudio size={17}/>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{item.title}</div>
                <div className="text-[11px] text-[var(--rammy-text-muted)]">
                  {item.kind === "video" ? "Audiogram video" : item.kind === "image" ? "Picture" : "Narration"}
                  {item.kind === "image" ? "" : ` · ${fmtDur(item.duration_seconds)}`} · {fmtSize(item.size)}
                  {item.chapters?.length ? ` · ${item.chapters.length} chapters` : ""} · {fmtWhen(item.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => open(item)}
                  className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)] transition-colors"
                  data-testid={`creation-play-${item.id}`}
                  aria-label="Play"
                >
                  {busyId === item.id
                    ? <L.Loader2 size={15} className="animate-spin"/>
                    : (activeId === item.id ? <L.X size={15}/> : <L.Play size={15}/>)}
                </button>
                <button
                  onClick={() => download(item)}
                  className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)] transition-colors"
                  data-testid={`creation-download-${item.id}`}
                  aria-label="Download"
                >
                  <L.Download size={15}/>
                </button>
                <button
                  onClick={() => setShareItem(item)}
                  className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)] transition-colors"
                  data-testid={`creation-share-${item.id}`}
                  aria-label="Share"
                  title="Download, copy a link, or send it to YouTube"
                >
                  <L.Share2 size={15}/>
                </button>
                {item.project_id && (
                  <button
                    onClick={() => {
                      try { sessionStorage.setItem("rammy-studio-open", item.project_id); } catch (_) { /* ignore */ }
                      window.dispatchEvent(new CustomEvent("rammy-open-studio-project", { detail: item.project_id }));
                      go?.("studio");
                    }}
                    className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)] transition-colors"
                    data-testid={`creation-open-project-${item.id}`}
                    aria-label="Open the project"
                    title="Open the project that made this"
                  >
                    <L.PenLine size={15}/>
                  </button>
                )}
                {confirmId === item.id ? (
                  <span className="inline-flex items-center gap-1">
                    <button
                      onClick={() => remove(item)}
                      className="px-2.5 h-8 rounded-full bg-red-500 text-white text-[11px] font-medium"
                      data-testid={`creation-delete-confirm-${item.id}`}
                    >
                      Remove
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-2 h-8 rounded-full text-[11px] text-[var(--rammy-text-sec)] hover:bg-[var(--rammy-surface-alt)]"
                      data-testid={`creation-delete-cancel-${item.id}`}
                    >
                      Keep
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmId(item.id)}
                    className="p-2 rounded-full hover:bg-red-50 text-[var(--rammy-text-muted)] hover:text-red-500 transition-colors"
                    data-testid={`creation-delete-${item.id}`}
                    aria-label="Remove"
                  >
                    <L.Trash2 size={15}/>
                  </button>
                )}
              </div>
            </div>

            {!!item.chapters?.length && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`creation-chapters-${item.id}`}>
                {item.chapters.map((c, i) => (
                  <span
                    key={i}
                    className="text-[10.5px] px-2 py-0.5 rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]"
                  >
                    {fmtStamp(c.start_seconds)} · {c.title}
                  </span>
                ))}
              </div>
            )}

            {activeId === item.id && activeUrl && (
              <div className="mt-3" data-testid={`creation-player-${item.id}`}>
                {item.kind === "video" ? (
                  <video src={activeUrl} controls autoPlay className="w-full max-h-[420px] rounded-xl bg-black"/>
                ) : item.kind === "image" ? (
                  <img
                    src={activeUrl}
                    alt={item.title}
                    className="w-full max-h-[520px] object-contain rounded-xl bg-black/5"
                    data-testid={`creation-image-${item.id}`}
                  />
                ) : (
                  <audio ref={audioRef} src={activeUrl} controls autoPlay className="w-full"/>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {shareItem && (
        <ShareSheet creation={shareItem} onClose={() => setShareItem(null)} />
      )}
    </div>
  );
}
